import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { divIcon } from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { BadgeCheckIcon, Building2Icon, CheckCircle2Icon, Clock3Icon, HomeIcon, MapPinIcon, MessageSquareIcon, NavigationIcon } from 'lucide-react';
import { SITE } from '../constants/site';
const SERVICE_LOCATIONS = [{
  name: 'Garland',
  position: [32.9126, -96.6389] as [number, number],
  primary: true
}, {
  name: 'Fate',
  position: [32.9412, -96.3811] as [number, number]
}, {
  name: 'Allen',
  position: [33.1032, -96.6706] as [number, number]
}, {
  name: 'Heath',
  position: [32.8368, -96.4775] as [number, number]
}, {
  name: 'Lavon',
  position: [33.0298, -96.4358] as [number, number]
}, {
  name: 'Lucas',
  position: [33.0812, -96.5819] as [number, number]
}, {
  name: 'Plano',
  position: [33.0198, -96.6989] as [number, number]
}, {
  name: 'Wylie',
  position: [33.0151, -96.5389] as [number, number]
}, {
  name: 'Murphy',
  position: [33.0151, -96.6131] as [number, number]
}, {
  name: 'Parker',
  position: [33.0565, -96.6203] as [number, number]
}, {
  name: 'Sachse',
  position: [32.9762, -96.5953] as [number, number]
}, {
  name: 'Rowlett',
  position: [32.9029, -96.5639] as [number, number]
}, {
  name: 'Mesquite',
  position: [32.7668, -96.5992] as [number, number]
}, {
  name: 'Rockwall',
  position: [32.9312, -96.4597] as [number, number]
}, {
  name: 'Richardson',
  position: [32.9483, -96.7299] as [number, number]
}, {
  name: 'Forney',
  position: [32.7482, -96.4722] as [number, number]
}, {
  name: 'Sunnyvale',
  position: [32.7987, -96.5617] as [number, number]
}];
interface MapViewportProps {
  position: [number, number];
}
function MapViewport({
  position
}: MapViewportProps) {
  const map = useMap();
  useEffect(() => {
    map.flyTo(position, 11, {
      duration: 0.65
    });
  }, [map, position]);
  return null;
}
function createPinIcon(isSelected: boolean, isPrimary: boolean) {
  const color = isSelected ? '#063a6e' : isPrimary ? '#4faa2f' : '#0b5cab';
  const size = isSelected ? 42 : 34;
  return divIcon({
    className: 'northface-map-pin',
    html: `<span style="display:flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;border-radius:9999px;background:${color};border:3px solid #ffffff;box-shadow:0 5px 14px rgba(6,58,110,.28);color:#ffffff;font-size:16px;font-weight:800;">+</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
}
export function ServiceAreaSection() {
  const [selectedArea, setSelectedArea] = useState('Garland');
  const selectedLocation = SERVICE_LOCATIONS.find(location => location.name === selectedArea) ?? SERVICE_LOCATIONS[0];
  const pins = useMemo(() => SERVICE_LOCATIONS.map(location => ({
    ...location,
    icon: createPinIcon(location.name === selectedArea, Boolean(location.primary))
  })), [selectedArea]);
  return <section id="area" className="w-full bg-white py-16 sm:py-24" data-id="element-400" data-mp="2lar0utgwukpe">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-401" data-mp="2gcfqmhmpe86n">
        <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(6,58,110,0.10)]" data-id="element-402" data-mp="2gbwh9pb4cs48">
          <div className="absolute inset-x-0 top-0 h-1.5 bg-nfgreen" data-id="element-403" data-mp="2q294jpabize2" />
          <div className="grid lg:grid-cols-[0.82fr_1.18fr]" data-id="element-404" data-mp="2q4c1kt9w1ssa">
            <div className="relative border-b border-slate-200 p-7 sm:p-10 lg:border-b-0 lg:border-r lg:p-12" data-id="element-405" data-mp="22dprkwgbpyfn">
              <div className="absolute right-0 top-0 h-44 w-44 rounded-bl-full bg-nfgreen/10" data-id="element-406" data-mp="1xev9xxu3efx4" />
              <div className="relative" data-id="element-407" data-mp="2cbm0rippajb4">
                <div className="flex flex-wrap items-center gap-3" data-id="element-408" data-mp="2c8ht2bd115fp">
                  <span className="inline-flex items-center gap-2 rounded-full bg-nfgreen/15 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.13em] text-nfgreen-dark" data-id="element-409" data-mp="27a6j80mqeuxh">
                    <MapPinIcon className="h-3.5 w-3.5" data-id="element-410" data-mp="3ipv4mnkc7lw6" />
                    Local & Licensed
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500" data-id="element-411" data-mp="1sbdowjjck8v4">
                    <BadgeCheckIcon className="h-4 w-4 text-nfblue" data-id="element-412" data-mp="1x9fhpw6krmjs" />
                    Texas License LI0026988
                  </span>
                </div>

                <h2 className="mt-6 max-w-md font-display text-3xl font-extrabold leading-[1.08] text-slate-950 sm:text-4xl" data-id="element-413" data-mp="26a3pwa9aesor">
                  Your local irrigation team, across North Texas.
                </h2>
                <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg" data-id="element-414" data-mp="2g6q78p2en1m1">
                  Garland is home base. From Dallas to Rowlett, our licensed team knows
                  the local soil, water pressure, and city requirements that keep your
                  system running right.
                </p>

                <div className="mt-8 grid grid-cols-2 gap-3 border-y border-slate-100 py-5" data-id="element-415" data-mp="2u0zlligv681a">
                  <div data-id="element-416" data-mp="2yyj0mkldyoua">
                    <p className="font-display text-2xl font-extrabold text-nfblue" data-id="element-417" data-mp="2yvww6ttskpga">{SERVICE_LOCATIONS.length}</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-418" data-mp="343d1p03e2hn6">Cities served</p>
                  </div>
                  <div className="border-l border-slate-100 pl-4" data-id="element-419" data-mp="2z4s1v1s1tzz9">
                    <p className="font-display text-2xl font-extrabold text-nfblue" data-id="element-420" data-mp="2v2wylrqk5ciu">DFW</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-421" data-mp="3rsee0yfcecxq">Local coverage</p>
                  </div>
                </div>

                <div className="mt-8" data-id="element-422" data-mp="3mvo0ahr0ftfh">
                  <div className="flex items-center justify-between gap-4" data-id="element-423" data-mp="3i2cp64f60qhr">
                    <p className="flex items-center gap-2 text-sm font-bold text-slate-800" data-id="element-424" data-mp="3e19ahl8ut0i4">
                      <HomeIcon className="h-4 w-4 text-nfgreen-dark" data-id="element-425" data-mp="3dzgceucb3olq" />
                      Explore our service area
                    </p>
                    <span className="text-xs font-medium text-slate-500" data-id="element-426" data-mp="9mt3prkqkcd9">Select a city</span>
                  </div>
                  <div className="mt-4 grid max-h-48 grid-cols-2 gap-2 overflow-y-auto pr-2 sm:grid-cols-3" data-id="element-427" data-mp="23w7vm25bcr1c">
                    {SERVICE_LOCATIONS.map((location, i) => {
                    const isSelected = location.name === selectedArea;
                    return <motion.button key={location.name} type="button" initial={{
                      opacity: 0,
                      y: 8
                    }} whileInView={{
                      opacity: 1,
                      y: 0
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.26,
                      delay: i * 0.04
                    }} onClick={() => setSelectedArea(location.name)} className={`group flex min-h-[52px] items-center justify-between rounded-xl border px-3 text-left text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-nfblue focus:ring-offset-2 ${isSelected ? 'border-nfblue bg-nfblue text-white shadow-md shadow-nfblue/20' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-nfblue/40 hover:bg-white hover:text-nfblue'}`} aria-pressed={isSelected} data-id="element-428" data-mp="1z46osvqwzsip" data-mpi={i}>
                          <span data-id="element-429" data-mp="18ljjwg516b11">{location.name}</span>
                          {isSelected ? <CheckCircle2Icon className="h-4 w-4" data-id="element-430" data-mp="18nw9nfh15pon" /> : <MapPinIcon className="h-4 w-4 text-nfgreen transition-colors group-hover:text-nfblue" data-id="element-431" data-mp="13ul2fd8b36w7" />}
                        </motion.button>;
                  })}
                  </div>
                </div>

                <motion.div key={selectedArea} initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.22
              }} className="mt-6 flex items-center gap-3 rounded-2xl bg-slate-950 p-4 text-white" data-id="element-432" data-mp="1djuuit35gpkb">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-nfgreen text-white" data-id="element-433" data-mp="3tdvbr8be94jc">
                    <NavigationIcon className="h-5 w-5" data-id="element-434" data-mp="32k1rrnqjvne2" />
                  </div>
                  <p className="text-sm leading-relaxed text-slate-200" data-id="element-435" data-mp="37iv3pbq6v6c4">
                    <strong className="text-white" data-id="element-436" data-mp="37h1xtynegq56">{selectedArea}, TX is actively served.</strong>{' '}
                    Select a pin to confirm coverage.
                  </p>
                </motion.div>

                <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-nfblue/15 bg-nfblue/5 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-437" data-mp="3har1elod4uft">
                  <div className="flex gap-3" data-id="element-438" data-mp="2ol1yu3w08mc3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-nfblue shadow-sm" data-id="element-439" data-mp="2oltwrld2qw5j">
                      <MessageSquareIcon className="h-4 w-4" data-id="element-440" data-mp="2yipx33stkbz0" />
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600" data-id="element-441" data-mp="8t4odkd4uf4h">
                      <strong className="block text-slate-900" data-id="element-442" data-mp="8v7d812ju27m">Don't see your area?</strong>
                      Text us your address — we'll quickly confirm availability.
                    </p>
                  </div>
                  <a href={SITE.smsHref} className="inline-flex shrink-0 items-center justify-center rounded-lg bg-nfblue px-3.5 py-2 text-xs font-bold text-white transition-colors hover:bg-nfblue-dark focus:outline-none focus:ring-2 focus:ring-nfblue focus:ring-offset-2" data-id="element-443" data-mp="1tb5gdm1ujryw">
                    Ask us
                  </a>
                </div>
              </div>
            </div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.98
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5
          }} className="relative min-h-[470px] bg-slate-100" data-id="element-444" data-mp="1ejo7927bg31l">
              <div className="absolute inset-x-0 top-0 z-[500] flex items-start justify-between gap-3 p-5 pointer-events-none" data-id="element-445" data-mp="2zyaprggexvuw">
                <div className="rounded-xl border border-white/80 bg-white/95 px-4 py-3 shadow-lg shadow-slate-900/10 backdrop-blur" data-id="element-446" data-mp="2v125nabpfhr0">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-900" data-id="element-447" data-mp="31sqsjz7vuu7z">
                    <span className="relative flex h-2.5 w-2.5" data-id="element-448" data-mp="3fsjgjms0ywc9">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-nfgreen opacity-60" data-id="element-449" data-mp="3frqkbfqozp8d" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-nfgreen" data-id="element-450" data-mp="3bp3pd5yq5o6y" />
                    </span>
                    Active coverage
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500" data-id="element-451" data-mp="2j065wzis1lcm">Licensed North Texas service</p>
                </div>
                <div className="hidden rounded-full bg-slate-950 px-3 py-1.5 text-xs font-bold text-white shadow-lg sm:block" data-id="element-452" data-mp="2wvi8ft36cg43">
                  {SERVICE_LOCATIONS.length} cities
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 z-[500] flex items-center gap-2 bg-slate-950/85 px-5 py-4 text-xs font-semibold text-white pointer-events-none" data-id="element-453" data-mp="20caydxdrd3ry">
                <Clock3Icon className="h-4 w-4 text-nfgreen-light" data-id="element-454" data-mp="1q5zxlpp0buk2" />
                Serving residential, commercial & HOA properties
              </div>
              <MapContainer center={selectedLocation.position} zoom={10} scrollWheelZoom={false} className="h-[470px] w-full" data-id="element-455" data-mp="1gapueymvhh4p">
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-456" data-mp="1l913v8999pme" />
                <MapViewport position={selectedLocation.position} data-id="element-457" data-mp="2dwwzsar03di4" />
                {pins.map(location => <Marker key={location.name} position={location.position} icon={location.icon} eventHandlers={{
                click: () => setSelectedArea(location.name)
              }} data-id="element-458" data-mp="1yzcz3g1504hr">
                    <Popup data-id="element-459" data-mp="njy60nc1twrx">
                      <div className="min-w-[150px] py-1" data-id="element-460" data-mp="njodot3j7dj2">
                        <strong data-id="element-461" data-mp="sl5aodiwafud">{location.name}, TX</strong>
                        <span className="mt-1 block text-xs" data-id="element-462" data-mp="2v6rfyepc3n16">Northface service area</span>
                      </div>
                    </Popup>
                  </Marker>)}
              </MapContainer>
              <div className="absolute bottom-5 right-5 z-[500] hidden rounded-xl bg-white/95 px-3 py-2 text-xs font-bold text-slate-700 shadow-lg backdrop-blur sm:flex sm:items-center sm:gap-2 pointer-events-none" data-id="element-463" data-mp="2lgy55fx0nbxz">
                <Building2Icon className="h-4 w-4 text-nfblue" data-id="element-464" data-mp="2bii9r0qyhgio" />
                Local coverage, not subcontracted
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}